import { sendAnalytics } from '@audio-network/analytics'
import {
	Button,
	Container,
	Heading,
	Paragraph,
} from '@audio-network/componentlibrary'
import { Play, Sparkle } from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'

import type { SearchType } from '@/components/pages/search/SearchSelector/SearchSelector.types'
import { getSearch } from '@/utils/window'

import styles from './BlockResults.module.css'

type BlockResultsVariant = Extract<SearchType, 'reference' | 'prompt'>

function blockResultsVariant(
	searchType: SearchType
): BlockResultsVariant | null {
	if (searchType === 'reference' || searchType === 'prompt') {
		return searchType
	}
	return null
}

const BLOCK_RESULTS_ICONS: Record<BlockResultsVariant, JSX.Element> = {
	reference: (
		<span className={clsx(styles.iconTile, styles.iconReference)}>
			<Play color="white" />
		</span>
	),
	prompt: (
		<span className={clsx(styles.iconTile, styles.iconPrompt)}>
			<Sparkle color="white" />
		</span>
	),
}

const BlockResults = ({
	searchType,
}: {
	searchType: SearchType
}): JSX.Element => {
	const { t } = useTranslation(['search'])

	const pathname = usePathname()
	const search = getSearch()

	const variant = blockResultsVariant(searchType)

	const handleLoginClick = (): void => {
		sendAnalytics({
			noun: 'Login',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Location:
					variant === 'reference'
						? 'Reference Search Panel'
						: 'Prompt Search Panel',
			},
		})
	}

	const handleCreateAccountClick = (): void => {
		sendAnalytics({
			noun: 'Create Account',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Location:
					variant === 'reference'
						? 'Reference Search Panel'
						: 'Prompt Search Panel',
			},
		})
	}

	return (
		<Container className={styles.container} maxWidth="large">
			{variant && (
				<>
					{BLOCK_RESULTS_ICONS[variant]}
					<Heading level={2} size="S">
						{t(
							variant === 'reference'
								? 'try-audio-search-title'
								: 'try-prompt-search-title'
						)}
					</Heading>
					<Paragraph className={styles.description}>
						{t(
							variant === 'reference'
								? 'try-audio-search-description'
								: 'try-prompt-search-description'
						)}
					</Paragraph>
				</>
			)}
			<div className={styles.buttonsWrapper}>
				<Button
					href={`/login?returnurl=${encodeURIComponent(
						`${pathname}${search}`
					)}`}
					onClick={handleLoginClick}
					variant="secondary"
				>
					{t('login')}
				</Button>
				<Button
					href="/registration"
					onClick={handleCreateAccountClick}
					data-testid="ai-search-create-account"
				>
					{t('create-account')}
				</Button>
			</div>
		</Container>
	)
}

export default BlockResults
